import type { StaticAnalysisReport } from '@realplc/types';

const RULES: Record<string, [string, string]> = {
  ST_REVIEW_SYMBOL_INDEX: ['源文件符号索引', 'Source-local symbol index'],
  ST_REVIEW_ARRAY_BOUNDS: ['数组常量下标边界', 'Constant array index bounds'],
  ST_REVIEW_CALL_GRAPH: ['源文件调用解析', 'Source-local call resolution'],
  ST_REVIEW_RECURSION: ['可能的递归环', 'Possible recursive cycles'],
  ST_REVIEW_DIVISION_BY_ZERO: ['常量除零', 'Constant division by zero'],
  ST_REVIEW_FOR_STEP_ZERO: ['FOR 零步长', 'FOR zero step'],
  ST_REVIEW_FOR_DIRECTION: ['FOR 边界方向', 'FOR direction'],
  ST_REVIEW_METRICS: ['代码指标', 'Code metrics'],
  ST_UNDECLARED_IDENTIFIER: ['未声明变量（现有检查）', 'Undeclared identifiers (existing check)'],
  ST_REVIEW_SOURCE_COVERAGE: ['源码覆盖范围', 'Source coverage'],
};
const cell = (value: unknown) => String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\|/g, '\\|').replace(/`/g, '\\`').replace(/[\r\n]/g, ' ');

/** Separate from the ordinary issue list: shadow findings never become repair errors. */
export function staticAnalysisMarkdown(value: unknown, zh: boolean): string {
  if (!value || typeof value !== 'object') return '';
  const report = value as StaticAnalysisReport;
  if (report.analyzerId !== 'realplc-st-analysis' || report.mode !== 'shadow' || !Array.isArray(report.evidence)) return '';
  const label = (id: string) => RULES[id]?.[zh ? 0 : 1] ?? id;
  const lines = [
    `## ${zh ? '补充静态检查（观察模式）' : 'Supplementary static analysis (observation mode)'}`, '',
    zh ? '新增规则仅供评审，不触发自动修复，也不代表原生编译通过。未检查的项不计为通过。' : 'New rules are review evidence only. They do not trigger automatic repair or establish a native compiler pass. Unchecked items are not passes.', '',
    zh ? '| 检查 | 程序块 | 实际覆盖 | 已判定 / 未判定位置 |' : '| Check | Block | Actual coverage | Assessed / unresolved sites |',
    '| --- | --- | --- | --- |',
  ];
  for (const each of report.evidence.slice(0, 100)) {
    const status = each.status === 'checked'
      ? (each.outcome === 'findings' ? (zh ? '已检查，有发现' : 'Checked, findings') : (zh ? '已检查，未发现' : 'Checked, no findings'))
      : each.status === 'unsupported' ? (zh ? '未支持 / 未完整判定' : 'Unsupported / incomplete')
        : each.status === 'failed' ? (zh ? '分析失败' : 'Analysis failed')
          : each.reason === 'not_applicable' ? (zh ? '不适用' : 'Not applicable') : (zh ? '已跳过' : 'Skipped');
    lines.push(`| ${cell(label(each.ruleId))} | ${cell(each.block || (zh ? '整份源码' : 'Whole source'))} | ${status} | ${each.assessedSites} / ${each.unresolvedSites} |`);
  }
  if (report.evidence.length > 100) lines.push('', zh ? `另有 ${report.evidence.length - 100} 条覆盖记录保存在完整报告中。` : `${report.evidence.length - 100} more coverage records are retained in the full report.`);
  const findings = (report.findings ?? []).filter(finding => finding.code !== 'ST_UNDECLARED_IDENTIFIER');
  if (findings.length) {
    lines.push('', `### ${zh ? '待评审发现' : 'Findings for review'}`, '');
    for (const finding of findings.slice(0, 100)) {
      lines.push(`- ${cell(finding.block)} · ${zh ? '第' : 'line'} ${finding.line}${zh ? '行' : ''} · ${cell(label(finding.code))}: ${cell(finding.expression)}`);
    }
    if (findings.length > 100) lines.push(zh ? `- 另有 ${findings.length - 100} 条发现保存在完整报告中。` : `- ${findings.length - 100} more findings are retained in the full report.`);
  }
  if (report.engineering?.callGraph) {
    const graph = report.engineering.callGraph;
    const names = new Map(graph.nodes.map(node => [node.id, node.name]));
    lines.push('', `### ${zh ? '源文件调用图' : 'Source-local call graph'}`, '',
      zh ? '仅限本次源文件；未执行语法或类型验证，未绑定外部库或跨文件符号。环表示 POU/类型之间可能递归，不证明运行路径实际执行。' : 'This source only; no syntax/type validation or external library/cross-file binding. Cycles indicate possible POU/type recursion, not proof of an executing runtime path.', '',
      graph.complete ? (zh ? '已解析本文件中观察到的全部调用。' : 'All observed calls resolved within this source.')
        : (zh ? '调用图不完整；未解析调用或未覆盖源码不能据此判定无递归。' : 'Incomplete graph: unresolved calls or uncovered source prevent a no-recursion conclusion.'), '',
      zh ? '| 调用方 | 调用目标 | 行 | 解析状态 |' : '| Caller | Call target | Line | Resolution |', '| --- | --- | --- | --- |');
    for (const edge of graph.edges.slice(0, 100)) {
      const resolution = edge.resolution === 'resolved' ? (zh ? '已解析' : 'Resolved') : (zh ? '未解析 / 未支持' : 'Unresolved / unsupported');
      lines.push(`| ${cell(names.get(edge.callerId) ?? edge.callerId)} | ${cell(edge.target)} | ${edge.line} | ${resolution} |`);
    }
    if (graph.edges.length > 100) lines.push('', zh ? `另有 ${graph.edges.length - 100} 条调用记录保存在完整报告中。` : `${graph.edges.length - 100} more calls are retained in the full report.`);
  }
  if (report.metrics?.length) {
    lines.push('', `### ${zh ? '程序块指标' : 'Block metrics'}`, '',
      zh ? '| 程序块 | 圈复杂度 | 最大嵌套 | 语句数 |' : '| Block | Cyclomatic complexity | Max nesting | Statements |', '| --- | --- | --- | --- |');
    for (const each of report.metrics) lines.push(`| ${cell(each.block)} | ${each.cyclomaticComplexity} | ${each.maxNestingDepth} | ${each.statementCount} |`);
  }
  return lines.join('\n');
}
